import React, { useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../../store';
import {
    fetchAffiliations,
    fetchFactions,
    fetchUnitGroups,
    fetchUnitGroupTree,
    createFaction,
    updateFaction,
    deleteFaction,
    createUnitGroup,
    createUnit,
    updateUnit,
    updateUnitPosition,
    reorderUnits,
    deleteUnit,
    setSelectedFaction,
    clearError,
} from '../../store/orbatSlice';
import {
    FactionCreate,
    FactionUpdate,
    UnitCreate,
    UnitUpdate,
    UnitGroupCreate,
} from '../../types/orbat';
import FactionList from './FactionList';
import OrbatCanvas from './OrbatCanvas';
import './OrbatView.scss';

/**
 * Main ORBAT Creator view component.
 * Displays a two-column layout with faction list and ORBAT tree visualization.
 */
const OrbatView: React.FC = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const dispatch = useDispatch<AppDispatch>();

    // Redux state
    const {
        affiliations,
        factions,
        selectedFactionId,
        factionsLoading,
        factionsError,
        unitGroups,
        unitGroupsLoading,
        selectedUnitGroupTree,
        treeLoading,
        treeError,
        isLoading,
        error,
    } = useSelector((state: RootState) => state.orbat);

    // Get currently selected faction
    const selectedFaction = factions.find((f) => f.id === selectedFactionId) || null;

    // Get affiliation name for the selected faction
    const selectedFactionAffiliationName = useMemo(() => {
        if (!selectedFaction) return undefined;
        const affiliation = affiliations.find((a) => a.id === selectedFaction.affiliation_id);
        return affiliation?.name;
    }, [selectedFaction, affiliations]);

    // Check if selected faction has any unit groups
    const selectedFactionHasUnitGroups = useMemo(() => {
        if (!selectedFactionId) return false;
        return unitGroups.some((ug) => ug.faction_id === selectedFactionId);
    }, [selectedFactionId, unitGroups]);

    // Fetch affiliations on mount (only once)
    useEffect(() => {
        dispatch(fetchAffiliations());
    }, [dispatch]);

    // Fetch factions when event changes
    useEffect(() => {
        if (eventId) {
            dispatch(fetchFactions(eventId));
        }
    }, [eventId, dispatch]);

    // Fetch unit groups when faction is selected
    useEffect(() => {
        if (eventId && selectedFactionId) {
            dispatch(fetchUnitGroups({ eventId, factionId: selectedFactionId }));
        }
    }, [eventId, selectedFactionId, dispatch]);

    // Fetch unit group tree when unit groups are loaded
    // For now, we'll auto-select the first unit group (or create one if needed)
    useEffect(() => {
        if (eventId && selectedFactionId && unitGroups.length > 0) {
            // Find the first unit group for this faction
            const factionUnitGroups = unitGroups.filter(
                (ug) => ug.faction_id === selectedFactionId
            );
            if (factionUnitGroups.length > 0) {
                dispatch(
                    fetchUnitGroupTree({
                        eventId,
                        unitGroupId: factionUnitGroups[0].id,
                    })
                );
            }
        }
    }, [eventId, selectedFactionId, unitGroups, dispatch]);


    // Handle faction selection
    const handleSelectFaction = useCallback(
        (factionId: string) => {
            dispatch(setSelectedFaction(factionId));
        },
        [dispatch]
    );

    // Handle faction creation
    const handleCreateFaction = useCallback(
        async (data: FactionCreate) => {
            if (!eventId) return;
            const result = await dispatch(createFaction({ eventId, data }));
            if (createFaction.fulfilled.match(result)) {
                // Auto-select the new faction
                dispatch(setSelectedFaction(result.payload.id));
            } else {
                throw new Error(result.payload as string);
            }
        },
        [eventId, dispatch]
    );

    // Handle faction update
    const handleUpdateFaction = useCallback(
        async (factionId: string, data: FactionUpdate) => {
            if (!eventId) return;
            const result = await dispatch(
                updateFaction({ eventId, factionId, data })
            );
            if (updateFaction.rejected.match(result)) {
                throw new Error(result.payload as string);
            }
        },
        [eventId, dispatch]
    );

    // Handle faction deletion
    const handleDeleteFaction = useCallback(
        async (factionId: string) => {
            if (!eventId) return;
            await dispatch(deleteFaction({ eventId, factionId }));
        },
        [eventId, dispatch]
    );

    // Handle unit group creation
    const handleCreateUnitGroup = useCallback(
        async (data: UnitGroupCreate) => {
            if (!eventId) return;
            const result = await dispatch(createUnitGroup({ eventId, data }));
            if (createUnitGroup.fulfilled.match(result)) {
                // Fetch the tree for the newly created unit group
                dispatch(
                    fetchUnitGroupTree({
                        eventId,
                        unitGroupId: result.payload.id,
                    })
                );
                // Refresh unit groups list
                dispatch(fetchUnitGroups({ eventId, factionId: data.faction_id }));
            } else {
                throw new Error(result.payload as string);
            }
        },
        [eventId, dispatch]
    );

    // Handle unit creation
    const handleCreateUnit = useCallback(
        async (data: UnitCreate) => {
            if (!eventId || !selectedUnitGroupTree) return;

            const result = await dispatch(createUnit({ eventId, data }));
            if (createUnit.fulfilled.match(result)) {
                // Refresh the tree
                dispatch(
                    fetchUnitGroupTree({
                        eventId,
                        unitGroupId: selectedUnitGroupTree.unit_group.id,
                    })
                );
            } else {
                throw new Error(result.payload as string);
            }
        },
        [eventId, selectedUnitGroupTree, dispatch]
    );

    // Handle unit update (edit)
    const handleUpdateUnit = useCallback(
        async (unitId: string, data: UnitUpdate) => {
            if (!eventId || !selectedUnitGroupTree) return;

            const result = await dispatch(
                updateUnit({
                    eventId,
                    unitId,
                    unitGroupId: selectedUnitGroupTree.unit_group.id,
                    data,
                })
            );

            if (updateUnit.fulfilled.match(result)) {
                // Refresh the tree
                dispatch(
                    fetchUnitGroupTree({
                        eventId,
                        unitGroupId: selectedUnitGroupTree.unit_group.id,
                    })
                );
            } else {
                throw new Error(result.payload as string);
            }
        },
        [eventId, selectedUnitGroupTree, dispatch]
    );

    // Handle unit position update (drag-drop)
    const handleUpdateUnitPosition = useCallback(
        async (unitId: string, newParentId: string | null, newOrder: number) => {
            if (!eventId || !selectedUnitGroupTree) return;

            const result = await dispatch(
                updateUnitPosition({
                    eventId,
                    unitId,
                    unitGroupId: selectedUnitGroupTree.unit_group.id,
                    data: {
                        upper_unit_id: newParentId,
                        unit_order: newOrder,
                    },
                })
            );

            // Always refresh the tree after position update
            dispatch(
                fetchUnitGroupTree({
                    eventId,
                    unitGroupId: selectedUnitGroupTree.unit_group.id,
                })
            );

            if (updateUnitPosition.rejected.match(result)) {
                throw new Error(result.payload as string);
            }
        },
        [eventId, selectedUnitGroupTree, dispatch]
    );

    // Handle sibling reordering (drag-to-reorder within the same parent)
    const handleReorderUnits = useCallback(
        async (orderedUnitIds: string[]) => {
            if (!eventId || !selectedUnitGroupTree) return;

            const result = await dispatch(
                reorderUnits({
                    eventId,
                    unitGroupId: selectedUnitGroupTree.unit_group.id,
                    orderedUnitIds,
                })
            );

            // Always refetch the tree so the new order is reflected (and any
            // failed reorder is rolled back to the server's state).
            dispatch(
                fetchUnitGroupTree({
                    eventId,
                    unitGroupId: selectedUnitGroupTree.unit_group.id,
                })
            );

            if (reorderUnits.rejected.match(result)) {
                throw new Error(result.payload as string);
            }
        },
        [eventId, selectedUnitGroupTree, dispatch]
    );

    // Handle unit deletion
    const handleDeleteUnit = useCallback(
        async (unitId: string) => {
            if (!eventId || !selectedUnitGroupTree) return;

            const result = await dispatch(
                deleteUnit({
                    eventId,
                    unitId,
                    unitGroupId: selectedUnitGroupTree.unit_group.id,
                })
            );

            // Only refresh the tree if deletion succeeded
            if (deleteUnit.fulfilled.match(result)) {
                dispatch(
                    fetchUnitGroupTree({
                        eventId,
                        unitGroupId: selectedUnitGroupTree.unit_group.id,
                    })
                );
            } else {
                throw new Error(result.payload as string);
            }
        },
        [eventId, selectedUnitGroupTree, dispatch]
    );

    // Handle error dismissal
    const handleDismissError = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Show loading state while fetching initial data
    if (factionsLoading && factions.length === 0) {
        return (
            <div className="orbat-container">
                <div className="orbat-loading">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading ORBAT...</span>
                    </Spinner>
                </div>
            </div>
        );
    }

    return (
        <div className="orbat-container">
            {/* Global error alert */}
            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={handleDismissError}
                    className="position-fixed"
                    style={{ top: 10, right: 10, zIndex: 1050 }}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
            )}

            {/* Faction List (Middle Column) */}
            <FactionList
                factions={factions}
                affiliations={affiliations}
                selectedFactionId={selectedFactionId}
                isLoading={factionsLoading || isLoading}
                error={factionsError}
                onSelectFaction={handleSelectFaction}
                onCreateFaction={handleCreateFaction}
                onUpdateFaction={handleUpdateFaction}
                onDeleteFaction={handleDeleteFaction}
            />

            {/* ORBAT Canvas (Right Column) */}
            <OrbatCanvas
                faction={selectedFaction}
                factionAffiliationName={selectedFactionAffiliationName}
                unitGroupTree={selectedUnitGroupTree}
                hasUnitGroups={selectedFactionHasUnitGroups}
                isLoading={treeLoading || unitGroupsLoading}
                error={treeError}
                onCreateUnitGroup={handleCreateUnitGroup}
                onCreateUnit={handleCreateUnit}
                onUpdateUnit={handleUpdateUnit}
                onUpdateUnitPosition={handleUpdateUnitPosition}
                onReorderUnits={handleReorderUnits}
                onDeleteUnit={handleDeleteUnit}
            />
        </div>
    );
};

export default OrbatView;
